import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Grid,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  CheckCircle as CheckIcon,
  LocalOffer as DiscountIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { APP_CHROME_OFFSET } from '../../theme/chromeOffset';
import { getSessionById, getSessionsAcceptingEnrollment, Session } from '../../services/sessionService';
import { calculateDiscount } from '../../services/discountService';
import { createPayPalOrder, capturePayPalPayment, CapturePayPalPaymentResponse } from '../../services/paymentService';
import { getEnrollmentsByUser, enrollInFreeSessions } from '../../services/enrollmentService';
import { useAuth } from '../../contexts/AuthContext';
// Standard PayPal payment component
import StandardPaymentComponent from '../../components/payments/StandardPaymentComponent';

interface PriceDetails {
  totalPrice: number;
  discountAmount: number;
  finalPrice: number;
}

function EnrollmentPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const { user } = authState;
  
  const [activeStep, setActiveStep] = useState(0);
  const [session, setSession] = useState<Session | null>(null);
  const [availableSessions, setAvailableSessions] = useState<Session[]>([]);
  const [selectedSessions, setSelectedSessions] = useState<number[]>([]);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscountCode, setAppliedDiscountCode] = useState('');
  const [discountCodeError, setDiscountCodeError] = useState<string | null>(null);
  const [priceDetails, setPriceDetails] = useState<PriceDetails>({
    totalPrice: 0,
    discountAmount: 0,
    finalPrice: 0,
  });
  const [loading, setLoading] = useState(true);
  const [priceLoading, setPriceLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // For tracking payment details
  const [/* paymentId */,  setPaymentId] = useState<string | null>(null);
  const [/* paymentComplete */, setPaymentComplete] = useState(false);
  
  // Calculate price without depending on any state to break the dependency cycle
  const calculatePrice = useCallback(async (sessionIds: number[], code: string) => {
    if (!sessionIds || sessionIds.length === 0) return;
    
    try {
      setPriceLoading(true);
      
      const result = await calculateDiscount(sessionIds, code);
      setPriceDetails(result);
    } catch (err) {
      console.error('Error calculating price:', err);
      // Don't show error to user for price calculation
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // Load session and available sessions for bundle
  useEffect(() => {
    const fetchSessionData = async () => {
      if (!sessionId) return;
      
      try {
        setLoading(true);
        
        // Get the primary session
        const sessionData = await getSessionById(parseInt(sessionId, 10));

        // Check if session has ended
        const now = new Date();
        const sessionEndDate = new Date(sessionData.endDate);
        if (sessionEndDate < now) {
          setError('This session has already ended and is no longer accepting enrollments.');
          setLoading(false);
          return;
        }

        // Ensure basePrice is a number
        if (sessionData && sessionData.basePrice) {
          sessionData.basePrice = parseFloat(String(sessionData.basePrice));
        }

        setSession(sessionData);
        
        const parsedSessionId = parseInt(sessionId, 10);
        
        // Add the primary session to selected sessions
        setSelectedSessions([parsedSessionId]);
        
        // Get other available sessions for potential bundle
        const availableSessionsData = await getSessionsAcceptingEnrollment();
        
        // Filter out the current session, already enrolled sessions, and ensure basePrice is a number for each session
        const currentSessionId = parsedSessionId;
        
        // Get user's enrollments to filter out sessions they're already enrolled in
        const enrollmentsData = await getEnrollmentsByUser(user?.id || 0);
        const enrolledSessionIds = enrollmentsData?.enrollments?.map(e =>
          typeof e.sessionId === 'string' ? parseInt(e.sessionId, 10) : e.sessionId
        ) || [];

        const otherSessions = availableSessionsData
          .filter(s => {
            // Compare as numbers to avoid type mismatches
            const availableSessionId = typeof s.id === 'string' ? parseInt(s.id, 10) : s.id;
            // Check if session has ended
            const sessionEndDate = new Date(s.endDate);
            const isNotEnded = sessionEndDate >= now;
            // Filter out current session, sessions user is already enrolled in, and ended sessions
            return availableSessionId !== currentSessionId && !enrolledSessionIds.includes(availableSessionId) && isNotEnded;
          })
          .map(s => ({
            ...s,
            basePrice: parseFloat(String(s.basePrice))
          }));

        setAvailableSessions(otherSessions);
        
        // Calculate initial price - do this after state updates to avoid infinite loops
        // Using an explicit array instead of selectedSessions to avoid the dependency cycle
        await calculateDiscount([parsedSessionId], '')
          .then(result => {
            setPriceDetails(result);
          })
          .catch(err => {
            console.error('Error calculating price:', err);
          });
      } catch (err) {
        console.error('Error fetching session data:', err);
        setError('Failed to load session information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessionData();
  }, [sessionId, user?.id]); // Remove calculatePrice from dependencies
  
  // Handle session selection change
  const handleSessionSelection = useCallback(async (sessionId: number, checked: boolean) => {
    let newSelected: number[];
    
    if (checked) {
      newSelected = [...selectedSessions, sessionId];
    } else {
      newSelected = selectedSessions.filter(id => id !== sessionId);
    }
    
    setSelectedSessions(newSelected);
    await calculatePrice(newSelected, appliedDiscountCode);
  }, [selectedSessions, appliedDiscountCode, calculatePrice]);
  
  // Handle discount code change
  const handleDiscountCodeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountCode(event.target.value);
    setDiscountCodeError(null);
  }, []);
  
  // Apply discount code
  const applyDiscountCode = useCallback(async () => {
    if (!discountCode.trim()) return;
    
    setDiscountCodeError(null);
    setPriceLoading(true);
    
    try {
      // Get current price without discount code
      const priceWithoutCode = await calculateDiscount(selectedSessions, '');
      
      // Get price with discount code
      const priceWithCode = await calculateDiscount(selectedSessions, discountCode);
      
      // Check if the discount was actually applied
      if (priceWithCode.discountAmount > priceWithoutCode.discountAmount) {
        setAppliedDiscountCode(discountCode);
        setPriceDetails(priceWithCode);
      } else {
        setDiscountCodeError('Invalid or expired discount code');
      }
    } catch (error) {
      console.error('Error applying discount code:', error);
      setDiscountCodeError('Error applying discount code');
    } finally {
      setPriceLoading(false);
    }
  }, [selectedSessions, discountCode]);
  
  // Clear discount code
  const clearDiscountCode = useCallback(async () => {
    setDiscountCode('');
    setAppliedDiscountCode('');
    setDiscountCodeError(null);
    await calculatePrice(selectedSessions, '');
  }, [selectedSessions, calculatePrice]);
  
  // Proceed to payment or enroll directly if free
  const proceedToPayment = useCallback(async () => {
    if (selectedSessions.length === 0) {
      setError('Please select at least one session to enroll in.');
      return;
    }

    // Check if the final price is $0
    if (priceDetails.finalPrice === 0) {
      // Free enrollment - directly enroll without payment
      try {
        setLoading(true);
        setError(null);
        await enrollInFreeSessions(selectedSessions, appliedDiscountCode || undefined);
        setActiveStep(2); // Skip to confirmation step
      } catch (err: unknown) {
        console.error('Error enrolling in free sessions:', err);
        const error = err as { response?: { data?: { message?: string } } };
        setError(error.response?.data?.message || 'Failed to enroll in sessions. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // Paid enrollment - proceed to payment step
      setActiveStep(1);
    }
  }, [selectedSessions, priceDetails.finalPrice, appliedDiscountCode]);
  
  // Handle payment completion
  const handlePaymentComplete = useCallback((_data: CapturePayPalPaymentResponse) => {
    setPaymentComplete(true);
    setActiveStep(2);
  }, []);
  
  // Create PayPal order
  const createOrder = useCallback(async () => {
    if (!user) {
      setPaymentError('You must be logged in to complete payment.');
      return { orderId: '', paymentId: '' };
    }
    
    try {
      setPaymentError(null);
      const result = await createPayPalOrder(selectedSessions, appliedDiscountCode);
      setPaymentId(result.paymentId);
      return { 
        orderId: result.orderId, 
        paymentId: result.paymentId 
      };
    } catch (err) {
      console.error('Error creating PayPal order:', err);
      setPaymentError('Failed to create payment. Please try again later.');
      return { orderId: '', paymentId: '' };
    }
  }, [user, selectedSessions, appliedDiscountCode]);
  
  // Capture payment after approval
  const capturePayment = useCallback(async (orderId: string, paymentId: string) => {
    try {
      setPaymentError(null);
      const result = await capturePayPalPayment(orderId, paymentId);
      handlePaymentComplete(result);
    } catch (err) {
      console.error('Error capturing payment:', err);
      setPaymentError('Failed to complete payment. Please try again.');
    }
  }, [handlePaymentComplete]);
  
  // Return to course selection
  const returnToSelection = useCallback(() => {
    setActiveStep(0);
  }, []);
  
  // Return to dashboard
  const returnToDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);
  
  // Mock PayPal button component for illustration
  useMemo(() => {
    const ButtonComponent = () => {
      const [processing, setProcessing] = useState(false);
      
      const handleMockPayment = async () => {
        setProcessing(true);
        try {
          const { orderId, paymentId } = await createOrder();
          if (orderId && paymentId) {
            // Simulate payment approval
            setTimeout(() => {
              capturePayment(orderId, paymentId);
              setProcessing(false);
            }, 2000);
          } else {
            setProcessing(false);
          }
        } catch (error) {
          setProcessing(false);
        }
      };
      
      return (
        <Button 
          variant="contained" 
          fullWidth 
          onClick={handleMockPayment}
          disabled={processing}
          sx={{ 
            bgcolor: '#0070ba', 
            color: 'white', 
            '&:hover': { bgcolor: '#003087' },
            py: 1.5,
            fontSize: '1.1rem'
          }}
        >
          {processing ? <CircularProgress size={24} color="inherit" /> : 'Pay with PayPal'}
        </Button>
      );
    };
    
    return ButtonComponent;
  }, [createOrder, capturePayment]);
  
  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Enrollment
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select your sessions and complete enrollment.
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mt: 4, mb: 4 }}>
          <Step>
            <StepLabel>Select Sessions</StepLabel>
          </Step>
          <Step>
            <StepLabel>Payment</StepLabel>
          </Step>
          <Step>
            <StepLabel>Confirmation</StepLabel>
          </Step>
        </Stepper>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <>
          {/* Step 1: Session Selection */}
          {activeStep === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Selected Session
                    </Typography>
                    
                    {session && (
                      <Paper 
                        elevation={1} 
                        sx={{ p: 2, mb: 2, borderRadius: 2 }}
                      >
                        <Typography variant="h6">{session.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {`${new Date(session.startDate).toLocaleDateString()} - ${new Date(session.endDate).toLocaleDateString()}`}
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {session.description}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          ${parseFloat(String(session.basePrice)).toFixed(2)}
                        </Typography>
                      </Paper>
                    )}
                    
                    {availableSessions.length > 0 && (
                      <>
                        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
                          Bundle with Other Sessions
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Add more sessions to your enrollment to receive a bundle discount!
                        </Typography>
                        
                        {availableSessions.map((otherSession) => (
                          <Paper 
                            key={otherSession.id} 
                            elevation={1} 
                            sx={{ 
                              p: 2, 
                              mb: 2, 
                              borderRadius: 2,
                              border: (theme) => `1px solid ${theme.palette.divider}`
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={selectedSessions.includes(otherSession.id)}
                                  onChange={(e) => handleSessionSelection(otherSession.id, e.target.checked)}
                                  color="primary"
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="h6">{otherSession.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {`${new Date(otherSession.startDate).toLocaleDateString()} - ${new Date(otherSession.endDate).toLocaleDateString()}`}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    {otherSession.description}
                                  </Typography>
                                  <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                                    ${parseFloat(String(otherSession.basePrice)).toFixed(2)}
                                  </Typography>
                                </Box>
                              }
                              sx={{ width: '100%', alignItems: 'flex-start', m: 0 }}
                            />
                          </Paper>
                        ))}
                      </>
                    )}

                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <TextField
                          label="Discount Code"
                          variant="outlined"
                          value={discountCode}
                          onChange={handleDiscountCodeChange}
                          fullWidth
                          error={!!discountCodeError}
                          helperText={
                            discountCodeError ||
                            (appliedDiscountCode ? `Applied: ${appliedDiscountCode}` : '')
                          }
                          sx={{ flex: 1 }}
                        />
                        <Button
                          variant="outlined"
                          onClick={applyDiscountCode}
                          disabled={priceLoading || !discountCode.trim()}
                          startIcon={<DiscountIcon />}
                          sx={{ mt: 0, height: '56px' }}
                        >
                          Apply
                        </Button>
                        {appliedDiscountCode && (
                          <Button
                            variant="outlined"
                            onClick={clearDiscountCode}
                            disabled={priceLoading}
                            color="secondary"
                            sx={{ mt: 0, height: '56px' }}
                          >
                            Clear
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                
                <Button
                  variant="contained"
                  size="large"
                  onClick={proceedToPayment}
                  endIcon={priceDetails.finalPrice === 0 ? <CheckIcon /> : <CartIcon />}
                  sx={{ mt: 2 }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : priceDetails.finalPrice === 0 ? (
                    'Enroll Now (Free)'
                  ) : (
                    'Proceed to Payment'
                  )}
                </Button>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    position: 'sticky',
                    // Sticks 24px below the viewport top, plus any global chrome pinned above the
                    // app (see theme/chromeOffset.ts) so it doesn't stick underneath that instead.
                    top: `calc(24px + ${APP_CHROME_OFFSET})`,
                  }}
                >
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Order Summary
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {priceLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                        <CircularProgress size={30} />
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">Regular Price:</Typography>
                          <Typography variant="body1">${priceDetails.totalPrice.toFixed(2)}</Typography>
                        </Box>
                        
                        {priceDetails.discountAmount > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1" color="success.main">
                              Discount:
                            </Typography>
                            <Typography variant="body1" color="success.main">
                              -${priceDetails.discountAmount.toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="h6">Total:</Typography>
                          <Typography variant="h6" color={priceDetails.finalPrice === 0 ? "success.main" : "primary"}>
                            {priceDetails.finalPrice === 0 ? 'FREE' : `$${priceDetails.finalPrice.toFixed(2)}`}
                          </Typography>
                        </Box>

                        {priceDetails.finalPrice === 0 && (
                          <Alert severity="success" sx={{ mt: 2 }}>
                            This is a free session! No payment required.
                          </Alert>
                        )}

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          {selectedSessions.length} session{selectedSessions.length !== 1 ? 's' : ''} selected
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          
          {/* Step 2: Payment */}
          {activeStep === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Payment
                    </Typography>
                    
                    {paymentError && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {paymentError}
                      </Alert>
                    )}
                    
                    <Typography variant="body1" paragraph>
                      Complete your payment securely with PayPal. You can use your PayPal account or any credit/debit card.
                    </Typography>
                    
                    <Box sx={{ mt: 3 }}>
                      <StandardPaymentComponent 
                        sessionIds={selectedSessions}
                        discountCode={appliedDiscountCode}
                        onPaymentComplete={handlePaymentComplete}
                        onPaymentError={setPaymentError}
                      />
                    </Box>
                  </CardContent>
                </Card>
                
                <Button 
                  variant="outlined"
                  onClick={returnToSelection}
                  sx={{ mt: 2 }}
                >
                  Back to Session Selection
                </Button>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    position: 'sticky',
                    // Sticks 24px below the viewport top, plus any global chrome pinned above the
                    // app (see theme/chromeOffset.ts) so it doesn't stick underneath that instead.
                    top: `calc(24px + ${APP_CHROME_OFFSET})`,
                  }}
                >
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Order Summary
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1">Regular Price:</Typography>
                      <Typography variant="body1">${priceDetails.totalPrice.toFixed(2)}</Typography>
                    </Box>
                    
                    {priceDetails.discountAmount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" color="success.main">
                          Discount:
                        </Typography>
                        <Typography variant="body1" color="success.main">
                          -${priceDetails.discountAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6">Total:</Typography>
                      <Typography variant="h6" color="primary">
                        ${priceDetails.finalPrice.toFixed(2)}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      {selectedSessions.length} session{selectedSessions.length !== 1 ? 's' : ''} selected
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          
          {/* Step 3: Confirmation */}
          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Enrollment Complete!
              </Typography>
              <Typography variant="body1" paragraph>
                Thank you for enrolling in MathWiz League. You now have access to your selected sessions.
              </Typography>
              {priceDetails.finalPrice === 0 && (
                <Alert severity="success" sx={{ maxWidth: 500, mx: 'auto', mb: 2 }}>
                  You have successfully enrolled in the free session{selectedSessions.length > 1 ? 's' : ''}!
                </Alert>
              )}
              <Typography variant="body1" paragraph>
                You'll receive a confirmation email shortly with all the details.
              </Typography>

              <Box sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={returnToDashboard}
                  startIcon={<SchoolIcon />}
                  sx={{ mr: 2 }}
                >
                  Go to Dashboard
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
    </MainLayout>
  );
}

export default EnrollmentPage;